import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID        = 'service_yk5pdo1';
const EMAILJS_TEMPLATE_ID       = 'template_hn5y8k6';   // → you
const EMAILJS_AUTOREPLY_ID      = 'template_52oe1al';   // → auto-reply to sender
const EMAILJS_PUBLIC_KEY        = 'ZaiUinzU0zrtKrcta';

export default function Contact() {
  const [role, setRole]       = useState('brand');
  const [focused, setFocused] = useState(null);
  const [status, setStatus]   = useState('idle');
  const [form, setForm]       = useState({ name: '', email: '', company: '', message: '' });
  const [errors, setErrors]   = useState({});

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())    errs.name    = 'Name is required';
    if (!form.email.trim())   errs.email   = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email';
    if (!form.message.trim()) errs.message = 'Please share your goals';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus('sending');
    try {
      const params = {
        // Sender info (covers common variable name variants used in templates)
        name:       form.name,
        email:      form.email,
        from_name:  form.name,
        from_email: form.email,
        reply_to:   form.email,

        // Auto-reply recipient
        to_name:    form.name,
        to_email:   form.email,

        // Extra context
        role:       role.charAt(0).toUpperCase() + role.slice(1),
        company:    form.company || '—',
        message:    form.message,
      };

      // Send both emails in parallel: notification to you + auto-reply to sender
      await Promise.all([
        emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID,   params, EMAILJS_PUBLIC_KEY),
        emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_AUTOREPLY_ID, params, EMAILJS_PUBLIC_KEY),
      ]);

      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const inputStyle = (name) => ({
    width: '100%',
    padding: '14px 16px',
    background: '#1a1a1a',
    border: '1px solid',
    borderColor: errors[name] ? '#e05555' : focused === name ? 'var(--accent)' : '#2a2a2a',
    borderRadius: '4px',
    color: 'var(--white)',
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    fontWeight: 400,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  });

  const labelStyle = {
    fontFamily: 'var(--font-body)',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.45)',
    marginBottom: '8px',
    display: 'block',
  };

  return (
    <section id="contact" className="section-pad" style={{ padding: '120px 56px', background: 'var(--hero-bg)' }}>
      <div className="contact-grid" style={{ maxWidth: '1280px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}>

        {/* Left */}
        <div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '20px' }}>
            Get In Touch
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 4vw, 60px)', fontWeight: 900, lineHeight: 1, color: 'var(--white)', marginBottom: '28px', textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
            Let's Build<br />Something<br />Great
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 400, color: 'rgba(255,255,255,0.45)', lineHeight: 1.8, marginBottom: '52px' }}>
            Ready to ascend? Whether you're a brand or a creator, we'd love to hear from you.
          </p>

          {[['Email', 'info@nileascendteam.com'], ['Headquarters', 'Columbus, OH']].map(([label, val]) => (
            <div key={label} style={{ marginBottom: '28px' }}>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: '6px' }}>{label}</div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 500, color: 'var(--white)' }}>{val}</div>
            </div>
          ))}

          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '8px', padding: '10px 16px', border: '1px solid #2a2a2a', borderRadius: '4px', background: '#111111' }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'rgba(255,255,255,0.45)', fontWeight: 500 }}>Typically responds within 24 hours</span>
          </div>
        </div>

        {/* Right — form */}
        <div>
          {status === 'success' ? (
            <div style={{ padding: '64px 40px', border: '1px solid #2a2a2a', textAlign: 'center', background: '#111111', borderRadius: '4px', animation: 'fadeIn 0.4s ease' }}>
              <div style={{ fontSize: '36px', marginBottom: '16px', color: 'var(--accent)' }}>✓</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 800, color: 'var(--white)', marginBottom: '12px', textTransform: 'uppercase' }}>Message Received</div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>We'll be in touch within 24 hours.</div>
              <button onClick={() => { setStatus('idle'); setForm({ name: '', email: '', company: '', message: '' }); }} style={{ marginTop: '28px', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #333', paddingBottom: '2px' }}>
                Send another message
              </button>
            </div>
          ) : (
            <form style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} onSubmit={handleSubmit} noValidate>

              {/* Role toggle */}
              <div>
                <label style={labelStyle}>I am a</label>
                <div style={{ display: 'flex', border: '1px solid #2a2a2a', borderRadius: '4px', overflow: 'hidden' }}>
                  {['brand', 'creator'].map((r) => (
                    <button key={r} type="button"
                      style={{ flex: 1, padding: '13px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: role === r ? 'var(--accent)' : '#1a1a1a', color: role === r ? '#0a0a0a' : 'rgba(255,255,255,0.4)', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                      onClick={() => setRole(r)}
                    >
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name + Email */}
              <div className="form-name-email" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {[{ label: 'Full Name', name: 'name', placeholder: 'Your name', type: 'text' }, { label: 'Email', name: 'email', placeholder: 'your@email.com', type: 'email' }].map((f) => (
                  <div key={f.name}>
                    <label style={labelStyle}>{f.label}</label>
                    <input type={f.type} name={f.name} placeholder={f.placeholder} value={form[f.name]} onChange={handleChange} style={inputStyle(f.name)} onFocus={() => setFocused(f.name)} onBlur={() => setFocused(null)} />
                    {errors[f.name] && <div style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: '#e05555', marginTop: '5px' }}>{errors[f.name]}</div>}
                  </div>
                ))}
              </div>

              {/* Company / Handle */}
              <div>
                <label style={labelStyle}>{role === 'brand' ? 'Company' : 'Social Handle'}</label>
                <input name="company" placeholder={role === 'brand' ? 'Your company name' : '@yourhandle'} value={form.company} onChange={handleChange} style={inputStyle('company')} onFocus={() => setFocused('company')} onBlur={() => setFocused(null)} />
              </div>

              {/* Goals */}
              <div>
                <label style={labelStyle}>Goals</label>
                <textarea name="message" placeholder="Campaign goals, budget range, timeline..." value={form.message} onChange={handleChange} style={{ ...inputStyle('message'), resize: 'vertical', minHeight: '120px' }} onFocus={() => setFocused('message')} onBlur={() => setFocused(null)} />
                {errors.message && <div style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: '#e05555', marginTop: '5px' }}>{errors.message}</div>}
              </div>

              {status === 'error' && (
                <div style={{ padding: '12px 16px', background: 'rgba(224,85,85,0.1)', border: '1px solid rgba(224,85,85,0.3)', borderRadius: '4px', fontFamily: 'var(--font-body)', fontSize: '13px', color: '#e05555' }}>
                  Something went wrong. Please try again or email info@nileascendteam.com directly.
                </div>
              )}

              <button type="submit" disabled={status === 'sending'}
                style={{ width: '100%', padding: '16px', background: status === 'sending' ? 'var(--accent-dark)' : 'var(--accent)', color: '#0a0a0a', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', border: 'none', borderRadius: '4px', cursor: status === 'sending' ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', transition: 'background 0.2s' }}>
                {status === 'sending' ? <><span className="spinner" style={{ borderColor: 'rgba(0,0,0,0.2)', borderTopColor: '#0a0a0a' }} />Sending...</> : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
